import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { isFeatureEnabled } from "../../services/FeatureFlagService";

const appInsights = new ApplicationInsights({
  config: { instrumentationKey: process.env.REACT_APP_INSTRUMENTATION_KEY },
});
appInsights.loadAppInsights();

const MSFT_TENANT = process.env.REACT_APP_AD_TENANT;
const GROUP_IDS = (process.env.REACT_APP_USER_AUTH_GROUP_ID || "").split(",").filter(Boolean);

// ===== In-memory memoization (B1) — no sessionStorage/localStorage =====
// Positive entitlement held in memory only; recomputed on every page reload.
// Keyed by account ID to prevent leaking results across account switches.
let _overageMemoResult: boolean | null = null;
let _overageMemoAccountId: string | null = null;

/**
 * Synchronously check whether the signed-in user has the App Role granted
 * via "MSFT All Accounts" group assignment in Enterprise Applications.
 * No Graph call — relies on the signed `roles` claim in the ID token.
 */
const userHasRole = (parentContext: any, roleName: string): boolean => {
  const account = parentContext?.authContext?.getAllAccounts()?.[0];
  const roles: string[] = account?.idTokenClaims?.roles ?? [];
  const hasRole = roles.includes(roleName);

  appInsights.trackTrace({
    message: `App role check role=${roleName} hasRole=${hasRole} oid=${account?.idTokenClaims?.oid ?? "(no-oid)"}`,
  });

  return hasRole;
};

/**
 * Build a response matching the shape callers expect:
 * { status: 200, data: { value: [...] } } when authorized
 * { status: 403, data: { value: [] } } when not
 */
const buildResponse = (hasRole: boolean): any =>
  hasRole
    ? { status: 200, data: { value: [{ id: "basic-access" }] } }
    : { status: 403, data: { value: [] } };

// ===== Identity classification (B4) =====

function normalizeIssuer(uri: string | undefined): string {
  if (!uri) return "";
  return uri
    .toLowerCase()
    .replace(/\/v2\.0\/?$/, "")   // strip /v2.0 suffix
    .replace(/\/+$/, "");         // strip trailing slashes
}

/**
 * Determines if the signed-in user is a home-tenant member (not a B2B guest).
 * Uses `acct` claim as primary signal (B4), with normalized idp/iss fallback.
 */
export function isHomeTenantUser(claims: any): boolean {
  if (!claims || claims.tid !== MSFT_TENANT) return false;

  // Primary: acct optional claim (0 = home, 1 = guest)
  if (claims.acct === 0) return true;
  if (claims.acct === 1) return false;

  // Fallback: normalized idp/iss comparison
  // v1.0 tokens omit idp for home-tenant users
  if (!claims.idp) return true;
  return normalizeIssuer(claims.idp) === normalizeIssuer(claims.iss);
}

// ===== Overage detection =====

/**
 * Tier 1: Spec-compliant overage signals (not flag-gated).
 * Returns true if token indicates a group overage occurred.
 */
export function hasGroupsOverage(claims: any): boolean {
  if (!claims) return false;
  // Require authoritative marker; _claim_sources alone is NOT sufficient (Q6)
  return (
    !claims.groups &&
    (Boolean(claims._claim_names?.groups) || claims.hasgroups === true)
  );
}

/**
 * Tier 2: Heuristic fallback — roles empty + home-tenant + flag enabled.
 * This IS flag-gated via IsGroupOverageFallbackEnabled.
 */
function needsOverageFallback(claims: any, roles: string[]): boolean {
  if (roles.length > 0) return false;
  if (!isFeatureEnabled("IsGroupOverageFallbackEnabled")) return false;
  return isHomeTenantUser(claims);
}

// ===== Graph fallback: POST /me/checkMemberGroups (B2) =====

export type FallbackResult = "granted" | "denied" | "error";

/**
 * Calls POST /me/checkMemberGroups using the signed-in user's delegated token.
 * Invariant: /me only — never accepts a client-supplied OID (#5).
 * Returns "granted" if user is a member, "denied" if not, "error" on Graph/auth failures.
 */
export async function checkMemberGroupsFallback(authClient: any): Promise<FallbackResult> {
  if (GROUP_IDS.length === 0) {
    appInsights.trackEvent({ name: "OverageFallbackGraphFailure", properties: { reason: "no-group-ids-configured" } });
    return "error";
  }

  const startTime = Date.now();

  try {
    const auth = authClient?.authContext;
    if (!auth) throw new Error("No auth context available");

    const accounts = auth.getAllAccounts();
    if (!accounts || accounts.length === 0) throw new Error("No account available");

    const account = accounts[0];
    const currentAccountId = account?.homeAccountId || account?.localAccountId || account?.username;

    // Return memoized result only if it matches the current signed-in account (B1).
    // Prevents leaking a previous user's result across account switches.
    if (_overageMemoResult !== null && currentAccountId && _overageMemoAccountId === currentAccountId) {
      return _overageMemoResult ? "granted" : "denied";
    }

    appInsights.trackEvent({ name: "OverageFallbackGraphStart", properties: { groupCount: String(GROUP_IDS.length) } });

    auth.setActiveAccount(account);
    const tokenResponse = await auth.acquireTokenSilent({ scopes: ["User.Read"], account });

    const response = await fetch("https://graph.microsoft.com/v1.0/me/checkMemberGroups", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${tokenResponse.accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ groupIds: GROUP_IDS }),
    });

    const latencyMs = Date.now() - startTime;

    if (!response.ok) {
      // Fail closed on all Graph HTTP errors (401/403/429/5xx) — surface as "error" for retry
      appInsights.trackEvent({
        name: "OverageFallbackGraphFailure",
        properties: { statusCode: String(response.status), latencyMs: String(latencyMs) },
      });
      // Do NOT memoize errors — allow retry
      return "error";
    }

    const data = await response.json();
    const isMember = Array.isArray(data.value) && data.value.length > 0;

    // Memoize in memory keyed by account (B1) — no persistent storage
    _overageMemoResult = isMember;
    _overageMemoAccountId = currentAccountId;

    appInsights.trackEvent({
      name: isMember ? "OverageFallbackGraphSuccess" : "OverageFallbackGraphDenied",
      properties: { latencyMs: String(latencyMs) },
    });

    return isMember ? "granted" : "denied";
  } catch (error: any) {
    const latencyMs = Date.now() - startTime;
    appInsights.trackEvent({
      name: "OverageFallbackGraphFailure",
      properties: { reason: error?.message || "unknown", latencyMs: String(latencyMs) },
    });
    // Return "error" so caller can show retry UI instead of a hard deny
    return "error";
  }
}

/**
 * Resets the in-memory memo — used for testing or when user logs out.
 */
export function resetOverageMemo(): void {
  _overageMemoResult = null;
  _overageMemoAccountId = null;
}

// ===== Public functions — signatures kept identical to old Graph callers =====
// Callers (V2_Shell, Shell, RequestAccess, FeedbackScreenshotDownloader) DO NOT change.

export const getGraphTransitiveMemberOf = async (parentContext: any): Promise<any> => {
  const account = parentContext?.authContext?.getAllAccounts()?.[0];
  const roles: string[] = account?.idTokenClaims?.roles ?? [];
  const claims = account?.idTokenClaims;

  // Fast path — role present in token
  if (roles.includes("BasicAccess")) {
    appInsights.trackTrace({ message: `App role check role=BasicAccess hasRole=true oid=${claims?.oid ?? "(no-oid)"}` });
    return buildResponse(true);
  }

  // Tier 1: Spec-compliant overage signal (not flag-gated)
  if (hasGroupsOverage(claims)) {
    appInsights.trackEvent({ name: "OverageFallbackEligible", properties: { tier: "1", userClass: "home" } });
    const result = await checkMemberGroupsFallback(parentContext);
    return buildResponse(result === "granted");
  }

  // Tier 2: Heuristic — no roles + home-tenant + flag enabled
  if (needsOverageFallback(claims, roles)) {
    appInsights.trackEvent({ name: "OverageFallbackEligible", properties: { tier: "2", userClass: "home" } });
    const result = await checkMemberGroupsFallback(parentContext);
    return buildResponse(result === "granted");
  }

  // No fallback applicable — flag disabled or guest user
  if (roles.length === 0 && isHomeTenantUser(claims) && !isFeatureEnabled("IsGroupOverageFallbackEnabled")) {
    appInsights.trackEvent({ name: "OverageFallbackFlagDisabled" });
  }

  appInsights.trackTrace({ message: `App role check role=BasicAccess hasRole=false oid=${claims?.oid ?? "(no-oid)"}` });
  return buildResponse(false);
};

export const getGraphTransitiveMemberOfAi = async (parentContext: any, _group: string): Promise<any> => {
  // For now AI gate also relies on BasicAccess. Later, add a separate "AiAccess" app role
  // and assign the specific MCAPS Help Engineering group to it.
  return buildResponse(userHasRole(parentContext, "BasicAccess"));
};

export const getGraphTransitiveMemberOfFeedback = async (parentContext: any): Promise<any> => {
  // FeedbackAccess role is already defined on the SPA app reg and assigned to MCAPSHelpFeedbackSupport group.
  return buildResponse(userHasRole(parentContext, "FeedbackAccess"));
};
